"use client";

import type { CSSProperties, MouseEvent } from "react";
import { Pause } from "lucide-react";
import { playToggleA11y } from "@/lib/a11y/play-toggle";

/**
 * TPlay — §03.4 "Play button" (03-Audio.dc.html)
 *
 * "Seven sizes, one component." This is that component. There used to be two:
 * `play-btn.tsx` carried a 44px default and a third resting treatment the spec
 * does not have (`--tls-surface-elevated` under a `--tls-border-strong` edge),
 * and the design-system preview rendered it beside this one, documenting the
 * inconsistency as though it were intentional (TES-2310).
 *
 * The ladder §03.4 names, and the only status a user needs at a glance:
 *
 *   ring   1.5px ink on transparent — the track is not loaded yet
 *   ink    solid `--tls-fg-strong`  — loaded, the next press is instant
 *   accent solid `--tls-brand`      — playing right now
 *
 * The detail page painted the ring on a ready control until TES-2136, so the
 * one action the page exists to sell read as the quietest element on the fold
 * while the Download carried the only fill. The ring had been reached for to
 * avoid "a second filled primary beside the accent Download pill", which
 * conflates *filled* with *accent*: §01.2's one-primary-per-panel rule governs
 * the six ACCENT roles, and ink spends none of them. The ink fill therefore
 * costs nothing against the 8% area cap and leaves the Download as the panel's
 * single accent primary. Do not restore the ring to buy accent budget.
 *
 * Buffering keeps whichever fill it started from rather than dropping to the
 * ring: §03.4 requires the control to report what you did on pointerdown, not
 * what the audio did, so `loading` reaches `aria-busy` only.
 *
 * No shadow. Those belong to the four floating surfaces (sheet, modal, mini
 * player, consent), never to a control on a page panel. The border is declared
 * in every state, transparent when filled, so the border-box geometry is
 * identical across the ladder and the disc never resizes as state changes.
 *
 * Two measured divergences from §03.4 that are RULED, not pending (TES-2312) —
 * do not "fix" either without reopening that issue:
 *
 *   Ring width. §03.4 says 1.5px and this declares 1.5px, but Blink floors
 *   border-width to whole CSS pixels and paints 1px; WebKit and Gecko paint
 *   the 1.5. Declaring 1px to "match what Chrome does" would take the ring
 *   off spec on mobile Safari, which is a large share of this audience, to
 *   make one engine's rounding look deliberate. `box-shadow: inset` would
 *   reach 1.5 in Blink, and is not worth a second paint mechanism on the
 *   system's most-pressed control for half a device pixel. 1.5 stays
 *   declared; §03.4's measures row records the Blink floor.
 *
 *   Glyph ratio. §03.4 measures the play glyph at size ÷ 3.4 and the pause
 *   bars at size ÷ 3.3; this renders 0.4 × size and 0.36 × size. The shipped
 *   proportions were drawn and verified at 56px in TES-2136, and §03.4's
 *   ratios were authored against a lucide Play in a tight viewBox, not
 *   against the T-shaped path below, whose own left inset carries §17.11's
 *   optical offset. The measures row is amended to the drawn numbers rather
 *   than the control being redrawn to a ratio nobody has looked at.
 */

/**
 * §03.4's size ladder is 28 · 40 · 44 · 52 · 56 · 64 · 76, and `size` stays a
 * plain number rather than a union of those seven: the mini player's 28 is
 * admitted only because the whole bar is also tappable, which a type cannot
 * check. 44 is the default because it is the WCAG 2.5.5 floor.
 */
const LADDER_DEFAULT = 44;

/**
 * The ground the control is painted ON, which decides every colour below.
 *
 * "canvas" is a normal page panel. "inverted" is a surface whose ground is
 * `--tls-fg-strong` — §04.2's expanded row plate is the only one so far — and
 * there ink and canvas swap roles, so the ring and the ready fill knock out.
 *
 * The accent swaps too, and that is the part a caller cannot get right by
 * hand: the inverted band follows the theme, so on it the raw accent is
 * correct in dark (5.6:1 on the near-white band) and forbidden in light
 * (2.39:1 on near-black, PRODUCT.md), and the lift is the exact reverse.
 * `--tls-brand-on-inverted` resolves that per theme in one place.
 */
export type TPlayGround = "canvas" | "inverted";

/** §03.4's three-way ladder, resolved against the ground it is painted on. */
function ladder(ground: TPlayGround, playing: boolean, loaded: boolean) {
  const inverted = ground === "inverted";
  const ink = inverted ? "var(--tls-canvas)" : "var(--tls-fg-strong)";
  const under = inverted ? "var(--tls-fg-strong)" : "var(--tls-canvas)";
  const accent = inverted ? "var(--tls-brand-on-inverted)" : "var(--tls-brand)";
  const accentFg = inverted ? "var(--tls-brand-on-inverted-fg)" : "var(--tls-brand-fg)";

  if (playing) return { fill: accent, glyph: accentFg, ring: "transparent" };
  if (loaded) return { fill: ink, glyph: under, ring: "transparent" };
  return { fill: "transparent", glyph: ink, ring: ink };
}

/**
 * §11.4's target floor, and the point where `size` stops being the whole
 * story. Below it the disc paints at `size` and `.tls-play-target` supplies
 * the 44px target as an out-of-flow pseudo-element — §02.4's "hit area padded
 * to 44" applied to a circle (TES-2312). At or above it the disc IS the
 * target, so the pad would be a 44px box inside a larger one: dead weight,
 * and a z-index:1 layer over the paint for no reason.
 *
 * The floor previously sat on the painted element as min-width/min-height,
 * which is exactly the collapse PRODUCT.md rules against under "the 44px
 * target is a padded wrapper, not a taller button": 28 and 40 both painted 44.
 *
 * A `size="css"` control cannot be compared against this at render time, so it
 * declares the class from its own rule if it ever drops below 44. The sound
 * row's rungs are 44 and 52, both at or above the floor.
 */
const TOUCH_FLOOR = 44;

interface TPlayProps {
  playing: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * One of §03.4's seven sizes. Defaults to 44, the WCAG 2.5.5 floor.
   *
   * `"css"` hands the diameter to the caller's stylesheet via
   * `--tls-play-size`, which the disc and the glyph both scale from. §04.1's
   * density axis is a media query, so the sound row's rung (44 at d1, 52 at
   * d0) is not knowable at render time on the server — and its SSR placeholder
   * has to paint at the same diameter as the live row or hydration jumps.
   */
  size?: number | "css";
  /**
   * False paints §03.4's 1.5px ring — "not loaded yet". Defaults to true: a
   * control that has resolved its audio is the common case, and the ring on a
   * ready control is exactly the regression TES-2136 fixed.
   */
  loaded?: boolean;
  /** Subject of the control (normally the track title) for the stable name. */
  title?: string;
  /** True while the track is resolving or buffering. Carried by aria-busy only. */
  loading?: boolean;
  /** The ground this control is painted on. See {@link TPlayGround}. */
  ground?: TPlayGround;
  /**
   * Render an inert `<span aria-hidden>` instead of a button: same box, same
   * paint, no name and no tab stop. For an SSR placeholder row that upgrades to
   * a live control on intersection — nobody should be offered a transport that
   * is not wired to audio yet.
   */
  decorative?: boolean;
  className?: string;
}

export function TPlay({
  onClick,
  playing,
  size = LADDER_DEFAULT,
  loaded = true,
  title,
  loading,
  ground = "canvas",
  decorative = false,
  className,
}: TPlayProps) {
  const cssSized = size === "css";
  // Below the floor the 44px target comes from the pseudo-element instead of
  // from the painted box; at or above it the disc already is the target. A
  // CSS-sized control cannot be measured here, so it opts in through the same
  // class from its own rule — and at 44 and above the pad is inert anyway.
  const padded = typeof size === "number" && size < TOUCH_FLOOR;

  const paint = ladder(ground, playing, loaded);
  const diameter = "var(--tls-play-size)";

  const style: CSSProperties = {
    // A number writes the property; "css" leaves it to the caller's rule.
    ...(cssSized ? null : { ["--tls-play-size" as string]: `${size}px` }),
    width: diameter,
    height: diameter,
    aspectRatio: "1 / 1",
    padding: 0,
    borderRadius: "50%",
    background: paint.fill,
    color: paint.glyph,
    border: `1.5px solid ${paint.ring}`,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    // An inline `position` would outrank the caller's own rule, and the sound
    // row repositions this control absolutely inside the expanded plate.
    ...(cssSized ? null : { position: "relative" as const }),
  };

  const glyph = playing ? (
    <Pause style={{ width: `calc(${diameter} * 0.36)`, height: `calc(${diameter} * 0.36)` }} />
  ) : (
    // Tesla-style T-shaped play (vertical stroke + crossbar + triangle).
    // The path's own left inset carries §17.11's optical offset, so this
    // glyph needs no playGlyphOffset margin the way a lucide Play does.
    <svg
      style={{ width: `calc(${diameter} * 0.4)`, height: `calc(${diameter} * 0.4)` }}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M8 5l12 7-12 7V5z" />
    </svg>
  );

  if (decorative) {
    return (
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      <span aria-hidden="true" className={className} style={style}>
        {glyph}
      </span>
    );
  }

  return (
    <button type="button"
      onClick={onClick}
      // Playback state is exposed via aria-pressed with a stable name (TES-1581).
      {...playToggleA11y({ playing, title, loading })}
      // data-icon-btn opts out of the global @media (pointer: coarse) button
      // min-height rule, which only stretches height and would render an oval
      // hit-target on a control whose width is its `size`. WCAG 2.5.5 is paid
      // by `.tls-play-target` instead — a square 44px pseudo-element centred
      // on the disc — so the paint keeps the diameter the caller asked for.
      data-icon-btn
      className={[
        // §03.4 measures press as scale(0.97) + opacity 0.85. Both, not one:
        // the scale alone was shipped and reads as a nudge rather than a press.
        "motion-safe:active:scale-[0.97] motion-safe:active:opacity-[0.85]",
        "tlsr-focus",
        padded ? "tls-play-target" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      style={{
        ...style,
        cursor: "pointer",
        // Both halves of the press ease together. Naming only `scale` here
        // left the opacity step snapping instantly against an 80ms scale,
        // which reads as a flicker rather than one press.
        transition:
          "scale var(--tls-duration-fast) var(--tls-ease), opacity var(--tls-duration-fast) var(--tls-ease)",
      }}
    >
      {glyph}
    </button>
  );
}
